import { Plus, Search, Users } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AddCustomerModal } from '@/modules/customer/components/AddCustomerModal'
import { CustomerActions } from '@/modules/customer/components/CustomerActions'
import { EditCustomerModal } from '@/modules/customer/components/EditCustomerModal'
import { useCustomerStore } from '@/modules/customer/store/customer.store'
import { AddLeadDialog } from '@/modules/marketing/components/leads/AddLeadDialog'
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog'
import CustomDateRangePicker from '@/shared/components/ui/CustomDateRangePicker'
import DashboardPeriodFilter from '@/shared/components/ui/DashboardPeriodFilter'
import { toast } from '@/shared/lib/toast'

import CreateQuoteDialog from '../components/quotations/CreateQuoteDialog'

export default function SalesCustomersPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const periodParam = searchParams.get('period') || 'all'
  const startDateParam = searchParams.get('startDate') || ''
  const endDateParam = searchParams.get('endDate') || ''

  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState(periodParam)
  const [customRange, setCustomRange] = useState({
    startDate: startDateParam,
    endDate: endDateParam,
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPeriod(periodParam)
    setCustomRange({
      startDate: startDateParam,
      endDate: endDateParam,
    })
  }, [periodParam, startDateParam, endDateParam])

  const handlePeriodChange = (val) => {
    setPeriod(val)
    const updated = new URLSearchParams(searchParams)
    if (val === 'all') {
      updated.delete('period')
      updated.delete('startDate')
      updated.delete('endDate')
    } else {
      updated.set('period', val)
      if (val !== 'custom') {
        updated.delete('startDate')
        updated.delete('endDate')
      }
    }
    setSearchParams(updated)
  }

  const handleCustomRangeChange = (range) => {
    setCustomRange(range)
    const updated = new URLSearchParams(searchParams)
    updated.set('period', 'custom')
    if (range.startDate) updated.set('startDate', range.startDate)
    if (range.endDate) updated.set('endDate', range.endDate)
    setSearchParams(updated)
  }

  // Per-customer actions state
  const [isCreateLeadOpen, setIsCreateLeadOpen] = useState(false)
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false)
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState(null)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [customerToDelete, setCustomerToDelete] = useState(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const {
    customers,
    meta,
    isLoading,
    fetchCustomers,
    updateCustomer,
    deleteCustomer,
  } = useCustomerStore()

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCustomers({
        page: 1,
        search,
        period,
        startDate: period === 'custom' ? customRange.startDate : undefined,
        endDate: period === 'custom' ? customRange.endDate : undefined,
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [search, period, customRange, fetchCustomers])

  const handleOpenCreateLead = () => {
    setIsCreateLeadOpen(true)
  }

  const handleCreateQuotation = (customer) => {
    if (customer?._id || customer?.id) {
      const id = customer._id || customer.id
      navigate(`/sales/leads/create-quotation/cust_${id}`, {
        state: { customer },
      })
      return
    }
    setIsCreateQuoteOpen(true)
  }

  const handleOpenEdit = (customer) => {
    setSelectedCustomerForEdit(customer)
    setIsEditOpen(true)
  }

  const handleOpenDelete = (customer) => {
    setCustomerToDelete(customer)
    setIsDeleteOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return
    try {
      setIsDeleting(true)
      await deleteCustomer(customerToDelete._id)
      setIsDeleteOpen(false)
      setCustomerToDelete(null)
    } catch {
      // Error handled by store
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="rounded-2xl sm:rounded-3xl border bg-white px-4 py-4 sm:px-7 sm:py-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold sm:text-4xl  text-slate-900">
              Customers
            </h1>
            <p className="mt-1 text-sm text-slate-500">Manage customers.</p>
          </div>

          <button
            type="button"
            onClick={() => setIsAddCustomerOpen(true)}
            className="flex items-center justify-center gap-2.5 rounded-full bg-slate-950 px-6 py-3.5 text-sm font-semibold text-white w-full sm:w-auto"
          >
            <Plus className="h-5 w-5" />
            Add Customer
          </button>
        </div>
      </div>

      {/* Customer Table */}
      <div className="overflow-hidden rounded-3xl border bg-white shadow-sm">
        {/* Search / Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b p-3.5 sm:px-5 sm:py-4">
          <div className="flex w-full items-center gap-3 rounded-full bg-slate-100 px-4 py-3 sm:px-5 sm:py-3.5 lg:w-[50%]">
            <Search className="h-5 w-5 text-slate-500 shrink-0" />

            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by company, contact person, email or phone..."
              className="w-full bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-500"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {period === 'custom' && (
              <CustomDateRangePicker
                startDate={customRange.startDate}
                endDate={customRange.endDate}
                onChange={handleCustomRangeChange}
              />
            )}

            <DashboardPeriodFilter
              value={period}
              onChange={handlePeriodChange}
            />

            <button
              type="button"
              className="flex items-center gap-2.5 rounded-full border bg-white px-4 py-2.5 sm:px-5 sm:py-3 text-xs sm:text-sm font-medium shadow-sm"
            >
              <Users className="h-4 w-4 text-slate-500" />
              {meta?.total ?? 0} of {meta?.total ?? 0}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1200px]">
            <thead>
              <tr className="border-b text-left">
                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Company Name
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Contact Person
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Phone Number
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Email ID
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Address
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  GST
                </th>

                <th className="px-5 py-5 text-xs font-bold uppercase tracking-wide text-slate-900">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-5 py-20 text-center text-sm text-slate-400"
                  >
                    Loading customers...
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-5 py-20 text-center text-sm text-slate-400"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer._id} className="border-b last:border-b-0">
                    <td className="px-5 py-5 text-sm font-semibold text-slate-900">
                      {customer.companyName}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonName}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonPhone}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.contactPersonEmail}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {[
                        customer.addressLine1,
                        customer.addressLine2,
                        customer.city,
                        customer.state,
                        customer.pincode,
                        customer.country,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </td>

                    <td className="px-5 py-5 text-sm text-slate-700">
                      {customer.gst || '-'}
                    </td>

                    <td className="px-5 py-5 text-sm">
                      <CustomerActions
                        customer={customer}
                        onCreateLead={handleOpenCreateLead}
                        onCreateQuotation={handleCreateQuotation}
                        onEdit={handleOpenEdit}
                        onDelete={handleOpenDelete}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t px-5 py-5">
          <p className="text-sm text-slate-500">
            Showing{' '}
            <span className="font-medium text-slate-700">
              {meta?.total
                ? `${(meta.page - 1) * meta.limit + 1}–${Math.min(
                    meta.page * meta.limit,
                    meta.total,
                  )}`
                : '0–0'}
            </span>{' '}
            of{' '}
            <span className="font-medium text-slate-700">
              {meta?.total ?? 0}
            </span>
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!meta || meta.page <= 1}
              onClick={() =>
                fetchCustomers({
                  page: meta.page - 1,
                  search,
                  period,
                  startDate:
                    period === 'custom' ? customRange.startDate : undefined,
                  endDate:
                    period === 'custom' ? customRange.endDate : undefined,
                })
              }
              className="rounded-full border px-5 py-3 text-sm text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              Previous
            </button>

            <button
              type="button"
              disabled
              className="h-11 w-11 rounded-full bg-slate-950 text-sm font-medium text-white"
            >
              {meta?.page ?? 1}
            </button>

            <button
              type="button"
              disabled={!meta || meta.page >= meta.totalPages}
              onClick={() =>
                fetchCustomers({
                  page: meta.page + 1,
                  search,
                  period,
                  startDate:
                    period === 'custom' ? customRange.startDate : undefined,
                  endDate:
                    period === 'custom' ? customRange.endDate : undefined,
                })
              }
              className="rounded-full border px-5 py-3 text-sm text-slate-700 disabled:cursor-not-allowed disabled:text-slate-400"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <AddCustomerModal
        open={isAddCustomerOpen}
        onOpenChange={setIsAddCustomerOpen}
      />

      {/* Create Lead Modal */}
      <AddLeadDialog
        open={isCreateLeadOpen}
        onOpenChange={setIsCreateLeadOpen}
        lead={null}
        module="sales"
        onSuccess={() => {
          toast.success('Lead created successfully')
        }}
      />

      {/* Select Lead & Create Quotation Dialog */}
      <CreateQuoteDialog
        open={isCreateQuoteOpen}
        onOpenChange={setIsCreateQuoteOpen}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        open={isEditOpen}
        onOpenChange={(open) => {
          setIsEditOpen(open)
          if (!open) setSelectedCustomerForEdit(null)
        }}
        customer={selectedCustomerForEdit}
        onUpdate={updateCustomer}
      />

      {/* Delete Customer Confirm Dialog */}
      <ConfirmDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        title="Delete Customer"
        description={`Are you sure you want to delete customer "${customerToDelete?.companyName}"? This action cannot be undone.`}
        confirmText="Delete"
        loading={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
